import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import {
  BackupInvitesData,
  backupInvitesClient,
  SendBackupInvitesResult,
} from '../api/backupInvites';
import { dashboardV2QueryKeys } from '../api/queryKeys';

import { backupSettingsKeys } from '@/routes/Agency/BackupInvites/hooks';
import { apiErrorMessage } from '../util/apiError';

import useDashboardToast, { DashboardToast } from './useDashboardToast';

// What the Invite backups modal shows before anything is sent: the ranked
// candidates, who is already a backup, and whether sending is allowed.
export const useBackupInvites = (jobId: number) =>
  useQuery<BackupInvitesData>({
    queryKey: dashboardV2QueryKeys.backupInvites(jobId),
    queryFn: () => backupInvitesClient.getBackupInvites(jobId),
    // Always fresh on open: the switch is global and the backup list changes
    // as workers answer, so a cached copy could offer someone already invited.
    staleTime: 0,
  });

// Plain words for BackupInviteService's skip reasons. An unknown reason is
// shown as "not invited" rather than its code.
const SKIP_REASON_LABELS: Record<string, string> = {
  already_invited: 'already invited',
  already_hired: 'already on the shift',
  blocked: 'blocked',
  invite_failed: "couldn't be invited",
  invite_not_sent: "message couldn't be sent",
};

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/** The toast after a send: who was invited, held and skipped, and why. */
export const backupInviteSummary = (
  result: SendBackupInvitesResult,
): Omit<DashboardToast, 'key'> => {
  const held = result.invited.filter((i) => i.message_status === 'held');
  const reasons = new Map<string, number>();
  result.skipped.forEach((s) => {
    const label = SKIP_REASON_LABELS[s.reason] ?? 'not invited';
    reasons.set(label, (reasons.get(label) ?? 0) + 1);
  });

  const parts: string[] = [];
  if (held.length > 0) parts.push(`${held.length} held until 8 AM`);
  if (result.skipped.length > 0) {
    const why = Array.from(reasons, ([label, n]) => `${n} ${label}`).join(', ');
    parts.push(`${result.skipped.length} skipped (${why})`);
  }

  return {
    tone: result.invited_count > 0 ? 'info' : 'warning',
    title: `Invited ${plural(result.invited_count, 'backup', 'backups')}`,
    message: parts.length ? `${parts.join('. ')}.` : 'Texts and pushes sent.',
  };
};

/**
 * Send backup invites, and flip the GLOBAL backup-invites switch.
 *
 * Both return null on failure and leave the API's own message in `error`, so
 * the modal can show it inline and stay open. Every outcome refetches the
 * modal's data: a 422 usually means the switch or the shift changed under it.
 */
export const useBackupInviteActions = (jobId: number) => {
  const queryClient = useQueryClient();
  const { notify } = useDashboardToast();
  const [pending, setPending] = useState<null | 'send' | 'switch'>(null);
  const [error, setError] = useState<string | null>(null);

  const send = useCallback(
    async (workerIds: number[]): Promise<SendBackupInvitesResult | null> => {
      setPending('send');
      setError(null);
      try {
        const result = await backupInvitesClient.sendBackupInvites(
          jobId,
          workerIds,
        );
        notify({
          key: `backup_invites:${jobId}:${Date.now()}`,
          ...backupInviteSummary(result),
        });
        // The whole dashboard: the panel's invite list and activity, the
        // card, and this modal's own data all move with a send.
        await queryClient.invalidateQueries({
          queryKey: dashboardV2QueryKeys.root,
        });
        return result;
      } catch (e) {
        setError(
          apiErrorMessage(e) ??
            'Could not send backup invites. Please try again.',
        );
        await queryClient.invalidateQueries({
          queryKey: dashboardV2QueryKeys.backupInvites(jobId),
        });
        return null;
      } finally {
        setPending(null);
      }
    },
    [jobId, notify, queryClient],
  );

  const setSwitch = useCallback(
    async (enabled: boolean): Promise<boolean> => {
      setPending('switch');
      setError(null);
      try {
        await backupInvitesClient.setSwitch(enabled);
        return true;
      } catch (e) {
        setError(
          apiErrorMessage(e) ??
            `Could not turn auto backups ${enabled ? 'on' : 'off'}. Please try again.`,
        );
        return false;
      } finally {
        setPending(null);
        // The master switch feeds far more than this popup: the candidates,
        // the header's "Auto backups on" pill, every calendar card's chip
        // (backups.enabled includes the switch) and the client/order/job
        // controls' "master switch is off" note. Refresh the whole dashboard
        // and the backup settings pages, or the cards keep the old state until
        // the 30 s poll (BH-01).
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: dashboardV2QueryKeys.root,
          }),
          queryClient.invalidateQueries({ queryKey: backupSettingsKeys.root }),
        ]);
      }
    },
    [jobId, queryClient],
  );

  return { send, setSwitch, pending, error, clearError: () => setError(null) };
};
